import {
  BRAND_NAME,
  DEFAULT_MERCHANT_RETURN_POLICY,
  MANUFACTURER_NAME,
} from 'lib/constants/organization';
import type { Product, WithContext } from 'schema-dts';
import type { SiteCategory } from 'types/common/SiteCategory';
import type { ShopifyProduct } from 'types/product/ShopifyProduct';
import { getPageUrl } from 'utils/getBaseUrl';
import { getSubscriptions } from 'utils/getSubscriptions';

import type { JsonLdOffer } from '../buildOffers';
import {
  buildOtpOffer,
  buildSubscriptionOffer,
  variantTitleOr,
} from '../buildOffers';
import { endOfNextYearISODate } from '../helpers/dates';
import { productId } from '../helpers/ids';

export interface BuildProductInput {
  shopifyProduct: ShopifyProduct;
  // Drives offer ordering so JSON-LD mirrors the PDP configurator.
  category: SiteCategory;
  name: string;
  description?: string;
  images: string[];
  pagePath: string;
  shippingCountries: readonly string[];
  aggregateRating?: { ratingValue: number; reviewCount: number };
}

export type BuiltProduct = WithContext<Product>;

const brandNode = () => ({ '@type': 'Brand', name: BRAND_NAME }) as const;

const manufacturerNode = () =>
  ({ '@type': 'Organization', name: MANUFACTURER_NAME }) as const;

const merchantReturnPolicy = (shippingCountries: readonly string[]) => ({
  '@type': 'MerchantReturnPolicy' as const,
  ...DEFAULT_MERCHANT_RETURN_POLICY,
  applicableCountry: [...shippingCountries],
});

interface JsonLdAggregateRating {
  '@type': 'AggregateRating';
  ratingValue: string;
  reviewCount: number;
}

const aggregateRatingNode = (
  rating: BuildProductInput['aggregateRating'],
): JsonLdAggregateRating | undefined =>
  rating
    ? {
        '@type': 'AggregateRating',
        ratingValue: rating.ratingValue.toFixed(2),
        reviewCount: rating.reviewCount,
      }
    : undefined;

const currencyFor = (shopifyProduct: ShopifyProduct): string =>
  shopifyProduct.variants[0]?.price.currencyCode ?? 'USD';

// Supplements: with subscriptions, list one subscription Offer per variant (in
// variant order) then a single OTP Offer using the first variant - mirroring
// SubscriptionMultiVariant, which shows the supply sizes then an OTP link.
// Without subscriptions, list one OTP Offer per variant in order.
const buildSupplementOffers = (
  input: BuildProductInput,
  currency: string,
  priceValidUntil: string,
): JsonLdOffer[] => {
  const { shopifyProduct, pagePath, name } = input;
  const pageUrl = getPageUrl(pagePath);
  const subscriptions = getSubscriptions(shopifyProduct);

  // Subscriptions keep the selling-plan title (which embeds supply size +
  // cadence); the single accompanying OTP offer is simply "One-time purchase".
  if (subscriptions.length > 0) {
    const subOffers = subscriptions.map((sub) =>
      buildSubscriptionOffer(
        sub,
        pageUrl,
        pagePath,
        currency,
        priceValidUntil,
        sub.sellingPlan.title,
      ),
    );
    const otpVariant = shopifyProduct.variants[0];
    const otpOffer = otpVariant
      ? [
          buildOtpOffer(
            otpVariant,
            pageUrl,
            pagePath,
            currency,
            priceValidUntil,
            'One-time purchase',
          ),
        ]
      : [];
    return [...subOffers, ...otpOffer];
  }

  // No subscriptions: one OTP offer per variant. A lone variant is just
  // "One-time purchase"; multiple variants keep their titles (e.g. sizes) so
  // the offers stay distinguishable, falling back to the product name.
  const { variants } = shopifyProduct;
  return variants.map((variant) =>
    buildOtpOffer(
      variant,
      pageUrl,
      pagePath,
      currency,
      priceValidUntil,
      variants.length === 1
        ? 'One-time purchase'
        : variantTitleOr(variant, name),
    ),
  );
};

// Skincare: only the full-size (canonical) product is represented - alternative
// formats (e.g. discovery size) are deliberately excluded. Emits a fixed
// "One-time purchase" offer, and when the product has any selling plans, a
// single "Subscribe and save" offer priced off the first plan (the plan's own
// name/cadence is ignored).
const buildSkincareOffers = (
  input: BuildProductInput,
  currency: string,
  priceValidUntil: string,
): JsonLdOffer[] => {
  const { shopifyProduct, pagePath } = input;
  const pageUrl = getPageUrl(pagePath);
  const offers: JsonLdOffer[] = [];

  const canonicalVariant = shopifyProduct.variants[0];
  if (canonicalVariant) {
    offers.push(
      buildOtpOffer(
        canonicalVariant,
        pageUrl,
        pagePath,
        currency,
        priceValidUntil,
        'One-time purchase',
      ),
    );
  }

  const [firstPlan] = getSubscriptions(shopifyProduct);
  if (firstPlan) {
    offers.push(
      buildSubscriptionOffer(
        firstPlan,
        pageUrl,
        pagePath,
        currency,
        priceValidUntil,
        'Subscribe and save',
      ),
    );
  }

  return offers;
};

// A single flat Product with an ordered offers list. The order matches what the
// PDP configurator displays for the product's category, and OTP vs subscription
// offers are distinguished by their human-readable name (no priceSpecification).
export const buildProduct = (input: BuildProductInput): BuiltProduct => {
  const {
    shopifyProduct,
    category,
    name,
    description,
    images,
    pagePath,
    shippingCountries,
    aggregateRating,
  } = input;
  const pageUrl = getPageUrl(pagePath);
  const currency = currencyFor(shopifyProduct);
  const priceValidUntil = endOfNextYearISODate();

  const offers =
    category === 'skincare'
      ? buildSkincareOffers(input, currency, priceValidUntil)
      : buildSupplementOffers(input, currency, priceValidUntil);

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': productId(pagePath),
    url: pageUrl,
    name,
    description,
    ...(images.length > 0 ? { image: images } : {}),
    brand: brandNode(),
    manufacturer: manufacturerNode(),
    offers,
    hasMerchantReturnPolicy: merchantReturnPolicy(shippingCountries),
    aggregateRating: aggregateRatingNode(aggregateRating),
  };
};
